import { useCallback, useEffect, useRef, type ReactNode } from "react";
import { useLocation } from "@tanstack/react-router";
import { driver, type Config, type DriveStep } from "driver.js";
import "driver.js/dist/driver.css";
import { isStepComplete, markStepComplete, ONBOARDING_IDS, type OnboardingStep } from "./constants";
import { OnboardingContext } from "./onboardingContext";
import { getIntroSteps } from "./steps/introSteps";
import { getNavigationSteps } from "./steps/navigationSteps";
import { dashboardOverviewSteps } from "./steps/dashboardOverviewSteps";
import { dashboardAnalyticsSteps } from "./steps/dashboardAnalyticsSteps";
import { dashboardHeatmapSteps } from "./steps/dashboardHeatmapSteps";
import { getDashboardCompareSteps } from "./steps/dashboardCompareSteps";
import { dashboardTeamComparisonSteps } from "./steps/dashboardTeamComparisonSteps";
import { playersSteps } from "./steps/playersSteps";
import { getPlayerProfileSteps } from "./steps/playerProfileSteps";
import { leaderboardsSteps } from "./steps/leaderboardsSteps";
import { tournamentOverviewSteps } from "./steps/tournamentOverviewSteps";
import { getTournamentCompareSteps } from "./steps/tournamentCompareSteps";
import { tournamentRankingSteps } from "./steps/tournamentRankingSteps";
import { teamsSteps } from "./steps/teamsSteps";
import { teamProfileSteps } from "./steps/teamProfileSteps";
import { teamPlayersSteps } from "./steps/teamPlayersSteps";
import { teamAnalysisSteps } from "./steps/teamAnalysisSteps";
import { teamCompareSteps } from "./steps/teamCompareSteps";
import { teamH2HSteps } from "./steps/teamH2HSteps";
import { scoutingPlayersSteps } from "./steps/scoutingPlayersSteps";
import { getKpisSteps } from "./steps/kpisCurrentSteps";
import { chatSteps } from "./steps/chatSteps";
import { settingsMultiplierSteps } from "./steps/settingsMultiplierSteps";
import { settingsReportSteps } from "./steps/settingsReportSteps";
import { dashboardPeriodDataSteps } from "./steps/dashboardPeriodDataSteps";
import { tournamentLinesSteps } from "./steps/tournamentLinesSteps";
import { useIsMobile } from "@/hooks/use-mobile";
import { openMobileMenu, closeMobileMenu } from "./mobileMenuControl";

function lockPage() {
    document.body.classList.add("onboarding-active");
}

function unlockPage() {
    document.body.classList.remove("onboarding-active");
}

let _smoothScroll = true;

function createTour(steps: DriveStep[], opts?: Omit<Config, "steps">) {
    const userOnDestroyed = opts?.onDestroyed;
    return driver({
        showProgress: true,
        allowClose: true,
        smoothScroll: _smoothScroll,
        popoverClass: "hockeyly-driver-popover",
        allowKeyboardControl: true,
        disableActiveInteraction: true,
        steps,
        ...opts,
        onDestroyed: (element, step, options) => {
            unlockPage();
            userOnDestroyed?.(element, step, options);
        },
    });
}

const el = (selector: string) => !!document.querySelector(selector);

function scrollToTopThenRun(callback: () => void) {
    document.querySelectorAll<HTMLElement>(".flex-1.min-h-0.overflow-auto, main.overflow-auto").forEach((el) => {
        el.scrollTo({ top: 0, behavior: "instant" });
    });
    window.scrollTo({ top: 0, behavior: "instant" });
    setTimeout(() => {
        requestAnimationFrame(() => {
            requestAnimationFrame(callback);
        });
    }, 50);
}

function waitAndDrive(isReady: () => boolean, steps: DriveStep[], onComplete: () => void, maxAttempts = 30) {
    let attempts = 0;
    const poll = () => {
        attempts++;
        if (isReady()) {
            setTimeout(() => {
                requestAnimationFrame(() => {
                    if (isReady()) {
                        const activeSteps = steps.filter((step) => {
                            if (typeof step.element !== "string") return true;
                            return document.querySelector(step.element) !== null;
                        });
                        if (activeSteps.length === 0) {
                            onComplete();
                            return;
                        }
                        lockPage();
                        scrollToTopThenRun(() => {
                            createTour(activeSteps, { onDestroyed: onComplete }).drive();
                        });
                    }
                });
            }, 600);
        } else if (attempts < maxAttempts) {
            setTimeout(poll, 300);
        } else {
            console.warn(
                `[Onboarding] Gave up waiting for tour target elements after ${maxAttempts} attempts (${
                    maxAttempts * 300
                }ms). Check that the ids referenced by this tour's steps are rendered on this route.`
            );
        }
    };
    poll();
}

interface OnboardingProviderProps {
    children: ReactNode;
}

export function OnboardingProvider({ children }: OnboardingProviderProps) {
    const isComplete = useCallback((step: OnboardingStep) => isStepComplete(step), []);
    const markComplete = useCallback((step: OnboardingStep) => markStepComplete(step), []);
    const location = useLocation();
    const isMobile = useIsMobile();

    useEffect(() => {
        _smoothScroll = !isMobile;
    }, [isMobile]);

    const beforeAnalyticsTourRef = useRef<(() => void) | null>(null);
    const beforeTournamentLinesRef = useRef<(() => void) | null>(null);
    const beforeChatTourRef = useRef<(() => void) | null>(null);

    const registerBeforeAnalyticsTour = useCallback((fn: () => void) => {
        beforeAnalyticsTourRef.current = fn;
        return () => {
            if (beforeAnalyticsTourRef.current === fn) {
                beforeAnalyticsTourRef.current = null;
            }
        };
    }, []);

    const registerBeforeTournamentLinesTour = useCallback((fn: () => void) => {
        beforeTournamentLinesRef.current = fn;
        return () => {
            if (beforeTournamentLinesRef.current === fn) {
                beforeTournamentLinesRef.current = null;
            }
        };
    }, []);

    const registerBeforeChatTour = useCallback((fn: () => void) => {
        beforeChatTourRef.current = fn;
        return () => {
            if (beforeChatTourRef.current === fn) {
                beforeChatTourRef.current = null;
            }
        };
    }, []);

    const startDashboardPeriodDataTour = useCallback(
        () =>
            waitAndDrive(
                () => el(`#${ONBOARDING_IDS.DASHBOARD_HEADER}`) && el(`#${ONBOARDING_IDS.PERIOD_DATA_NAV}`),
                dashboardPeriodDataSteps,
                () => markComplete("DASHBOARD_PERIOD_DATA")
            ),
        [markComplete]
    );

    const startTournamentLinesTour = useCallback(() => {
        beforeTournamentLinesRef.current?.();
        waitAndDrive(
            () =>
                el(`#${ONBOARDING_IDS.TOURNAMENT_LINES_OPPONENT_SELECT}`) &&
                el(`#${ONBOARDING_IDS.TEAM_H2H_MATCH_SELECTOR}`),
            tournamentLinesSteps,
            () => markComplete("TOURNAMENT_LINES")
        );
    }, [markComplete]);

    const startDashboardAnalyticsTour = useCallback(() => {
        beforeAnalyticsTourRef.current?.();
        waitAndDrive(
            () =>
                el("[data-dashboard-loaded='true']") &&
                el("[data-analytics-loaded='true']") &&
                el(`#${ONBOARDING_IDS.ANALYTICS_VIEW_MODE}`) &&
                el(`#${ONBOARDING_IDS.ANALYTICS_DATA_AREA}`),
            dashboardAnalyticsSteps,
            () => markComplete("DASHBOARD_ANALYTICS")
        );
    }, [markComplete]);

    const startDashboardHeatmapTour = useCallback(
        () =>
            waitAndDrive(
                () => el("[data-dashboard-loaded='true']") && el(`#${ONBOARDING_IDS.HEATMAP_CHART} canvas`),
                dashboardHeatmapSteps,
                () => markComplete("DASHBOARD_HEATMAP")
            ),
        [markComplete]
    );

    const startDashboardOverviewTour = useCallback(
        () =>
            waitAndDrive(
                () => el(`#${ONBOARDING_IDS.DASHBOARD_HEADER}`),
                dashboardOverviewSteps,
                () => markComplete("DASHBOARD_OVERVIEW"),
                20
            ),
        [markComplete]
    );

    const startDashboardCompareTour = useCallback(
        () =>
            waitAndDrive(
                () =>
                    el("[data-dashboard-loaded='true']") &&
                    el(`#${ONBOARDING_IDS.COMPARE_ENGINE}`) &&
                    el(`#${ONBOARDING_IDS.COMPARE_PLAYER_SELECT}`),
                getDashboardCompareSteps(isMobile),
                () => markComplete("DASHBOARD_COMPARE")
            ),
        [markComplete, isMobile]
    );

    const startDashboardTeamComparisonTour = useCallback(
        () =>
            waitAndDrive(
                () => el("[data-dashboard-loaded='true']") && el(`#${ONBOARDING_IDS.TEAM_KPI}`),
                dashboardTeamComparisonSteps,
                () => markComplete("DASHBOARD_TEAM_COMPARISON")
            ),
        [markComplete]
    );

    const startPlayersTour = useCallback(
        () =>
            waitAndDrive(
                () =>
                    el("[data-players-loaded='true']") &&
                    el(`#${ONBOARDING_IDS.PLAYERS_FILTERS}`) &&
                    el(`#${ONBOARDING_IDS.PLAYERS_GOALKEEPERS}`) &&
                    el(`#${ONBOARDING_IDS.PLAYERS_SORT}`),
                playersSteps,
                () => markComplete("PLAYERS")
            ),
        [markComplete]
    );

    const startPlayerProfileTour = useCallback(
        () =>
            waitAndDrive(
                () =>
                    el("[data-player-loaded='true']") &&
                    el(`#${ONBOARDING_IDS.PLAYER_HEADER}`) &&
                    el(`#${ONBOARDING_IDS.PLAYER_SKILLS}`) &&
                    el(`#${ONBOARDING_IDS.PLAYER_KPI_SELECTOR}`),
                getPlayerProfileSteps(isMobile),
                () => markComplete("PLAYER_PROFILE")
            ),
        [markComplete, isMobile]
    );

    const startLeaderboardsTour = useCallback(
        () =>
            waitAndDrive(
                () =>
                    el("[data-leaderboards-loaded='true']") &&
                    el(`#${ONBOARDING_IDS.LEADERBOARDS_TABLE}`) &&
                    el(`#${ONBOARDING_IDS.LEADERBOARDS_NAV}`),
                leaderboardsSteps,
                () => markComplete("LEADERBOARDS")
            ),
        [markComplete]
    );

    const startTournamentOverviewTour = useCallback(
        () =>
            waitAndDrive(
                () =>
                    el("[data-tournament-loaded='true']") &&
                    el(`#${ONBOARDING_IDS.TOURNAMENT_SELECTOR}`) &&
                    el(`#${ONBOARDING_IDS.TOURNAMENT_NAV}`) &&
                    el(`#${ONBOARDING_IDS.TOURNAMENT_METRICS}`),
                tournamentOverviewSteps,
                () => markComplete("TOURNAMENT")
            ),
        [markComplete]
    );

    const startTournamentCompareTour = useCallback(
        () =>
            waitAndDrive(
                () =>
                    el("[data-tournament-loaded='true']") &&
                    el(`#${ONBOARDING_IDS.TOURNAMENT_VIEW_MODE}`) &&
                    el(`#${ONBOARDING_IDS.COMPARE_ENGINE}`),
                getTournamentCompareSteps(isMobile),
                () => markComplete("TOURNAMENT_COMPARE")
            ),
        [markComplete, isMobile]
    );

    const startTournamentRankingTour = useCallback(
        () =>
            waitAndDrive(
                () =>
                    el("[data-tournament-loaded='true']") &&
                    el(`#${ONBOARDING_IDS.TOURNAMENT_STAT_CATEGORY}`) &&
                    el(`#${ONBOARDING_IDS.TOURNAMENT_RANKING_TABLE}`),
                tournamentRankingSteps,
                () => markComplete("TOURNAMENT_RANKING")
            ),
        [markComplete]
    );

    const startTeamsTour = useCallback(
        () =>
            waitAndDrive(
                () =>
                    el("[data-teams-loaded='true']") &&
                    el(`#${ONBOARDING_IDS.TEAMS_SORT}`) &&
                    el(`#${ONBOARDING_IDS.TEAMS_GRID}`),
                teamsSteps,
                () => markComplete("TEAMS")
            ),
        [markComplete]
    );

    const startTeamProfileTour = useCallback(
        () =>
            waitAndDrive(
                () =>
                    el("[data-team-profile-loaded='true']") &&
                    el(`#${ONBOARDING_IDS.TEAM_PROFILE_HEADER}`) &&
                    el(`#${ONBOARDING_IDS.TEAM_PROFILE_NAV}`) &&
                    el(`#${ONBOARDING_IDS.TEAM_PROFILE_METRICS}`),
                teamProfileSteps,
                () => markComplete("TEAM_PROFILE")
            ),
        [markComplete]
    );

    const startTeamPlayersTour = useCallback(
        () =>
            waitAndDrive(
                () =>
                    el("[data-team-players-loaded='true']") &&
                    el(`#${ONBOARDING_IDS.PLAYERS_SORT}`) &&
                    el(`#${ONBOARDING_IDS.TEAM_PLAYERS_LIST}`),
                teamPlayersSteps,
                () => markComplete("TEAM_PLAYERS")
            ),
        [markComplete]
    );

    const startTeamAnalysisTour = useCallback(
        () =>
            waitAndDrive(
                () =>
                    el("[data-team-analysis-loaded='true']") &&
                    el(`#${ONBOARDING_IDS.TEAM_ANALYSIS_GOALKEEPERS}`) &&
                    el(`#${ONBOARDING_IDS.TEAM_ANALYSIS_PLAYERS}`),
                teamAnalysisSteps,
                () => markComplete("TEAM_ANALYSIS")
            ),
        [markComplete]
    );

    const startTeamCompareTour = useCallback(
        () =>
            waitAndDrive(
                () =>
                    el("[data-team-compare-loaded='true']") &&
                    el(`#${ONBOARDING_IDS.COMPARE_ENGINE}`) &&
                    el(`#${ONBOARDING_IDS.COMPARE_PLAYER_SELECT}`),
                teamCompareSteps,
                () => markComplete("TEAM_COMPARE")
            ),
        [markComplete]
    );

    const startTeamH2HTour = useCallback(
        () =>
            waitAndDrive(
                () => el(`#${ONBOARDING_IDS.TEAM_H2H_MATCH_SELECTOR}`),
                teamH2HSteps,
                () => markComplete("TEAM_H2H")
            ),
        [markComplete]
    );

    const startScoutingPlayersTour = useCallback(
        () =>
            waitAndDrive(
                () =>
                    el("[data-scouting-players-loaded='true']") &&
                    el(`#${ONBOARDING_IDS.SCOUTING_TOURNAMENT}`) &&
                    el(`#${ONBOARDING_IDS.SCOUTING_NAV}`) &&
                    el(`#${ONBOARDING_IDS.SCOUTING_PLAYERS_TABLE}`),
                scoutingPlayersSteps,
                () => markComplete("SCOUTING_PLAYERS")
            ),
        [markComplete]
    );

    const startKpisCurrentTour = useCallback(() => {
        const isDefault = window.location.pathname === "/kpis/default";
        waitAndDrive(
            () =>
                el(`#${ONBOARDING_IDS.KPIS_NAV}`) &&
                el(`#${ONBOARDING_IDS.KPIS_CURRENT_INFO}`) &&
                el(`#${ONBOARDING_IDS.KPIS_METHODOLOGY}`),
            getKpisSteps(isDefault),
            () => markComplete("KPI_CURRENT")
        );
    }, [markComplete]);

    const startChatTour = useCallback(() => {
        beforeChatTourRef.current?.();
        setTimeout(() => {
            waitAndDrive(
                () => el(`#${ONBOARDING_IDS.CHAT_SIDEBAR}`) && el(`#${ONBOARDING_IDS.CHAT_MAIN}`),
                chatSteps,
                () => markComplete("CHAT")
            );
        }, 350);
    }, [markComplete]);

    const startSettingsMultiplierTour = useCallback(
        () =>
            waitAndDrive(
                () =>
                    el(`#${ONBOARDING_IDS.SETTINGS_NAV}`) &&
                    el(`#${ONBOARDING_IDS.SETTINGS_HEADER}`) &&
                    el(`#${ONBOARDING_IDS.SETTINGS_LIST}`),
                settingsMultiplierSteps,
                () => markComplete("SETTINGS_MULTIPLIER")
            ),
        [markComplete]
    );

    const startSettingsReportTour = useCallback(
        () =>
            waitAndDrive(
                () => el(`#${ONBOARDING_IDS.SETTINGS_NAV}`) && el(`#${ONBOARDING_IDS.SETTINGS_REPORT_HEADER}`),
                settingsReportSteps,
                () => markComplete("SETTINGS_REPORT")
            ),
        [markComplete]
    );

    const startNavigationTour = useCallback(() => {
        const run = () => {
            scrollToTopThenRun(() => {
                lockPage();
                createTour(getNavigationSteps(isMobile), {
                    onDestroyed: () => {
                        if (isMobile) closeMobileMenu();
                        markComplete("NAVIGATION");
                        const path = window.location.pathname;
                        if (path === "/dashboard/analytics" && !isStepComplete("DASHBOARD_ANALYTICS")) {
                            setTimeout(startDashboardAnalyticsTour, 400);
                        } else if (path === "/dashboard/heatmap" && !isStepComplete("DASHBOARD_HEATMAP")) {
                            setTimeout(startDashboardHeatmapTour, 400);
                        } else if (path === "/dashboard/compare" && !isStepComplete("DASHBOARD_COMPARE")) {
                            setTimeout(startDashboardCompareTour, 400);
                        } else if (
                            path === "/dashboard/teamcomparison" &&
                            !isStepComplete("DASHBOARD_TEAM_COMPARISON")
                        ) {
                            setTimeout(startDashboardTeamComparisonTour, 400);
                        } else if (!isStepComplete("DASHBOARD_OVERVIEW")) {
                            setTimeout(startDashboardOverviewTour, 400);
                        } else if (path === "/dashboard/period-data" && !isStepComplete("DASHBOARD_PERIOD_DATA")) {
                            setTimeout(startDashboardPeriodDataTour, 400);
                        }
                    },
                }).drive();
            });
        };
        if (isMobile) {
            openMobileMenu();
            setTimeout(run, 350);
        } else {
            run();
        }
    }, [
        isMobile,
        markComplete,
        startDashboardOverviewTour,
        startDashboardAnalyticsTour,
        startDashboardHeatmapTour,
        startDashboardCompareTour,
        startDashboardTeamComparisonTour,
        startDashboardPeriodDataTour,
    ]);

    const startIntroTour = useCallback(() => {
        const run = () => {
            scrollToTopThenRun(() => {
                lockPage();
                createTour(getIntroSteps(isMobile), {
                    onDestroyed: () => {
                        if (isMobile) closeMobileMenu();
                        markComplete("INTRO");
                        if (!isStepComplete("NAVIGATION")) setTimeout(startNavigationTour, 400);
                    },
                }).drive();
            });
        };
        if (isMobile) {
            openMobileMenu();
            setTimeout(run, 350);
        } else {
            run();
        }
    }, [markComplete, startNavigationTour, isMobile]);

    useEffect(() => {
        unlockPage();
        if (!isComplete("INTRO")) {
            const id = setTimeout(startIntroTour, 600);
            return () => clearTimeout(id);
        }
        if (!isComplete("NAVIGATION")) {
            const id = setTimeout(startNavigationTour, 600);
            return () => clearTimeout(id);
        }

        const path = location.pathname;

        const onAnalytics = path === "/dashboard/analytics";
        const onHeatmap = path === "/dashboard/heatmap";
        const onCompare = path === "/dashboard/compare";
        const onTeamCompar = path === "/dashboard/teamcomparison";
        const onPeriodData = path === "/dashboard/period-data";
        const onDashboardGen =
            path.startsWith("/dashboard") && !onAnalytics && !onHeatmap && !onCompare && !onTeamCompar && !onPeriodData;

        const dashboardRoutes: Array<[boolean, OnboardingStep, () => void]> = [
            [onAnalytics, "DASHBOARD_ANALYTICS", startDashboardAnalyticsTour],
            [onHeatmap, "DASHBOARD_HEATMAP", startDashboardHeatmapTour],
            [onCompare, "DASHBOARD_COMPARE", startDashboardCompareTour],
            [onTeamCompar, "DASHBOARD_TEAM_COMPARISON", startDashboardTeamComparisonTour],
            [onPeriodData, "DASHBOARD_PERIOD_DATA", startDashboardPeriodDataTour],
            [onDashboardGen, "DASHBOARD_OVERVIEW", startDashboardOverviewTour],
        ];
        for (const [matches, step, start] of dashboardRoutes) {
            if (matches && !isComplete(step)) {
                const id = setTimeout(start, 600);
                return () => clearTimeout(id);
            }
        }

        const exactRoutes: Array<[string, OnboardingStep, () => void]> = [
            ["/players", "PLAYERS", startPlayersTour],
            ["/leaderboards", "LEADERBOARDS", startLeaderboardsTour],
            ["/tournament/overview", "TOURNAMENT", startTournamentOverviewTour],
            ["/tournament/compare", "TOURNAMENT_COMPARE", startTournamentCompareTour],
            ["/tournament/ranking", "TOURNAMENT_RANKING", startTournamentRankingTour],
            ["/teams", "TEAMS", startTeamsTour],
            ["/scouting/players", "SCOUTING_PLAYERS", startScoutingPlayersTour],
            ["/kpis/current", "KPI_CURRENT", startKpisCurrentTour],
            ["/kpis/default", "KPI_CURRENT", startKpisCurrentTour],
            ["/chat", "CHAT", startChatTour],
            ["/settings/multiplier", "SETTINGS_MULTIPLIER", startSettingsMultiplierTour],
            ["/settings/report", "SETTINGS_REPORT", startSettingsReportTour],
            ["/tournament/lines", "TOURNAMENT_LINES", startTournamentLinesTour],
        ];
        for (const [route, step, start] of exactRoutes) {
            if (path === route && !isComplete(step)) {
                const id = setTimeout(start, 600);
                return () => clearTimeout(id);
            }
        }

        if (path.startsWith("/player/") && !isComplete("PLAYER_PROFILE")) {
            const id = setTimeout(startPlayerProfileTour, 600);
            return () => clearTimeout(id);
        }

        const patternRoutes: Array<[RegExp, OnboardingStep, () => void]> = [
            [/^\/teams\/[^/]+$/, "TEAM_PROFILE", startTeamProfileTour],
            [/^\/teams\/[^/]+\/players$/, "TEAM_PLAYERS", startTeamPlayersTour],
            [/^\/teams\/[^/]+\/analysis$/, "TEAM_ANALYSIS", startTeamAnalysisTour],
            [/^\/teams\/[^/]+\/compare$/, "TEAM_COMPARE", startTeamCompareTour],
            [/^\/teams\/[^/]+\/comparison$/, "TEAM_H2H", startTeamH2HTour],
        ];
        for (const [pattern, step, start] of patternRoutes) {
            if (pattern.test(path) && !isComplete(step)) {
                const id = setTimeout(start, 600);
                return () => clearTimeout(id);
            }
        }
    }, [
        location.pathname,
        isComplete,
        startIntroTour,
        startNavigationTour,
        startDashboardOverviewTour,
        startDashboardAnalyticsTour,
        startDashboardHeatmapTour,
        startDashboardCompareTour,
        startDashboardTeamComparisonTour,
        startPlayersTour,
        startPlayerProfileTour,
        startLeaderboardsTour,
        startTournamentOverviewTour,
        startTournamentCompareTour,
        startTournamentRankingTour,
        startTeamsTour,
        startTeamProfileTour,
        startTeamPlayersTour,
        startTeamAnalysisTour,
        startTeamCompareTour,
        startTeamH2HTour,
        startScoutingPlayersTour,
        startKpisCurrentTour,
        startChatTour,
        startSettingsMultiplierTour,
        startSettingsReportTour,
        startDashboardPeriodDataTour,
        startTournamentLinesTour,
    ]);

    return (
        <OnboardingContext.Provider
            value={{
                startIntroTour,
                startNavigationTour,
                startDashboardOverviewTour,
                startDashboardAnalyticsTour,
                startDashboardHeatmapTour,
                startDashboardCompareTour,
                startDashboardTeamComparisonTour,
                startPlayersTour,
                startPlayerProfileTour,
                startLeaderboardsTour,
                startTournamentOverviewTour,
                startTournamentCompareTour,
                startTournamentRankingTour,
                startTeamsTour,
                startTeamProfileTour,
                startTeamPlayersTour,
                startTeamAnalysisTour,
                startTeamCompareTour,
                startTeamH2HTour,
                startScoutingPlayersTour,
                startKpisCurrentTour,
                startChatTour,
                startSettingsMultiplierTour,
                startSettingsReportTour,
                markComplete,
                isComplete,
                registerBeforeAnalyticsTour,
                registerBeforeTournamentLinesTour,
                registerBeforeChatTour,
                startDashboardPeriodDataTour,
                startTournamentLinesTour,
            }}
        >
            {children}
        </OnboardingContext.Provider>
    );
}
